import { AuthApi } from '@/apis/auth';
import { DeviceApi } from '@/apis/device';
import { EApiBaseCode, ESessionErrorCode } from '@/constants';
import { hydrateUserProfileFromApi } from '@/providers/auth/hydrate-user-profile';
import { useUserInfoStore } from '@/store/user/slice';
import {
    reportGuestSessionAttempt,
    reportGuestSessionFail,
    reportGuestSessionFailFromError,
    reportGuestSessionSuccess,
} from '@/utils/arms/guest-session-events';
import { getOrCreateDeviceUuid, getOrCreateInstallSeq } from '@/utils/device/device-credentials';
import { pickLandingDyeParamsFromUrl } from '@/utils/device/pick-landing-dye-params';
import { getTouristDeviceIdForRequest, persistTouristDeviceId } from '@/utils/device/server-device-id';
import {
    hasAccountOperation,
    notifySessionLoginCompleted,
    recordSessionEstablished,
    registerSessionBootstrapRunner,
    registerSessionLoginRunner,
    waitForAccountOperation,
} from '@/utils/fetch/session-gate';
import { setSessionReadyCookie } from '@/utils/fetch/session-ready-cookie';
import { readSessionExpiresAt, saveSessionExpiry } from '@/utils/fetch/session-storage';
import { syncArmsConfig } from '@/utils/sync-arms-config';
import { registerTrackerParams } from '@/utils/tracker';

interface IRegisteredDevice {
    device_id: string;
    device_uuid: string;
    install_seq: string;
}

let registerPromise: Promise<IRegisteredDevice> | null = null;
let loginPromise: Promise<void> | null = null;
let establishSessionPromise: Promise<void> | null = null;
let bootstrapPromise: Promise<void> | null = null;

/** 每轮注册一次，保留染色四参；首访 login 与其他启动调用共享该任务。 */
function registerDevice(): Promise<IRegisteredDevice> {
    if (!registerPromise) {
        registerPromise = (async () => {
            const [device_uuid, install_seq] = await Promise.all([getOrCreateDeviceUuid(), getOrCreateInstallSeq()]);
            const landingDyeParams = pickLandingDyeParamsFromUrl();
            const res = await DeviceApi.postRegisterDevice({
                device_uuid,
                install_seq,
                async_landing: true,
                ...(landingDyeParams ?? {}),
            });
            if (res.code !== EApiBaseCode.SUCCESS || !res.data?.device_id) {
                throw new Error(res.message || ESessionErrorCode.DEVICE_REGISTER_FAILED);
            }
            persistTouristDeviceId(res.data.device_id);
            return { device_id: res.data.device_id, device_uuid, install_seq };
        })().catch(error => {
            // 失败任务不缓存，允许后续实际调用重新注册。
            registerPromise = null;
            throw error;
        });
    }
    return registerPromise;
}

/** 冷启、过期恢复与静默换发共用单飞；账号操作等待期间不提前占用 loginPromise。 */
export function loginSession(forceRefresh: boolean): Promise<void> {
    if (hasAccountOperation()) {
        return waitForAccountOperation().then(result => {
            if (result?.accountSucceeded && !result.expiryPersisted) {
                throw new Error(ESessionErrorCode.SESSION_EXPIRY_NOT_PERSISTED);
            }
            const expiresAt = readSessionExpiresAt();
            if (result?.accountSucceeded === true && expiresAt !== null && expiresAt > Date.now()) {
                return Promise.resolve();
            }
            return loginSession(forceRefresh);
        });
    }
    if (loginPromise) {
        return loginPromise;
    }
    loginPromise = (async () => {
        const cachedDeviceId = await getTouristDeviceIdForRequest();
        const device_id = cachedDeviceId ?? (await registerDevice()).device_id;
        const res = await AuthApi.postLogin({ device_id, force_refresh: forceRefresh });
        if (res.code !== EApiBaseCode.SUCCESS || !res.data?.user_id) {
            throw new Error(res.message || ESessionErrorCode.AUTH_LOGIN_FAILED);
        }
        const expiryPersisted = saveSessionExpiry(res.data.access_expire_in);
        recordSessionEstablished(expiryPersisted);
        if (!expiryPersisted) {
            throw new Error(ESessionErrorCode.SESSION_EXPIRY_NOT_PERSISTED);
        }
        setSessionReadyCookie();
        const { user_id, login_source = 0 } = res.data;
        useUserInfoStore.getState().updateUserInfo({ user_id, login_source });
        useUserInfoStore.getState().setIsLogin(true);
        syncArmsConfig();
    })().finally(() => {
        loginPromise = null;
        notifySessionLoginCompleted();
    });
    return loginPromise;
}

/** 仅返回一次性初始化，不把续期任务暴露为 landing 初始化等待。 */
export function getEstablishSessionInflight(): Promise<void> | null {
    return establishSessionPromise;
}

/** 注册与所需 login 并行；有缓存设备时，login 不等待 register、埋点或资料。 */
export function establishSession(): Promise<void> {
    if (establishSessionPromise) {
        return establishSessionPromise;
    }
    reportGuestSessionAttempt();
    let registerFailed = false;
    const expiresAt = readSessionExpiresAt();
    const registration = registerDevice().catch(error => {
        registerFailed = true;
        reportGuestSessionFail({ fail_reason: 'device_register_failed' });
        throw error;
    });
    const login = expiresAt === null || expiresAt <= Date.now() ? loginSession(expiresAt !== null) : Promise.resolve();
    establishSessionPromise = (async () => {
        // 两个任务立即安装错误处理；有设备时注册失败不取消独立运行的 login。
        const [device] = await Promise.all([registration, login]);
        const { user_id, login_source } = useUserInfoStore.getState().userInfo;
        reportGuestSessionSuccess({ user_id, login_source, tourist_device_id: device.device_id });
        await registerTrackerParams({
            user_id: useUserInfoStore.getState().userInfo.user_id,
            device_uuid: device.device_uuid,
            install_seq: device.install_seq,
        });
        syncArmsConfig();
    })().catch(error => {
        if (!registerFailed) {
            reportGuestSessionFailFromError(error);
        }
        establishSessionPromise = null;
        throw error;
    });
    return establishSessionPromise;
}

/** Strict Mode 与原首访门闩共用初始化；profile hydrate 不纳入账号操作等待。 */
export function runSessionBootstrap(): Promise<void> {
    if (!bootstrapPromise) {
        bootstrapPromise = (async () => {
            await establishSession();
            await hydrateUserProfileFromApi();
        })().catch(error => {
            bootstrapPromise = null;
            throw error;
        });
    }
    return bootstrapPromise;
}

registerSessionBootstrapRunner(establishSession);
registerSessionLoginRunner(loginSession, () => loginPromise);
